import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button, DialogContent } from '@mui/material';
import { darken } from 'polished';
import styled, { useTheme } from 'styled-components';

import { TenantEntities } from '@/features/canvas/api/tenant-sso';
import { editGroupMembers, getGroupMembers, importMembers } from '@/features/publicLinks/api';
import StyledButton from '@/features/ui/components/Button/Button';
import { Dialog } from '@/features/ui/components/Dialog';
import { useShowToast } from '@/hooks/useShowToast';

const GroupMembersDialog = ({
  onClose,
  groupId,
  members,
}: {
  groupId: number;
  members: TenantEntities['viewers'];
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { showToast } = useShowToast();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isListOpen, setIsListOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<TenantEntities['viewers']>([]);
  const [memberEntities, setMemberEntities] = useState(members);
  const listRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(event.target as Node)) {
      setIsListOpen(false);
    }

    const id = (event.target as HTMLElement)?.id;
    const match = id.match(/^add-member#(\d+)$/);

    if (match) {
      const memberId = match[1];
      void handleAddMember(memberEntities.find(item => item.id === Number(memberId))!);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMembers]);

  const { refetch } = useQuery(getGroupMembers.name, () => getGroupMembers(groupId ?? 0), {
    onSuccess: (data: TenantEntities['viewers']) => {
      setIsOpen(true);
      setSelectedMembers(data);
      setMemberEntities(members.filter(item => !data.some(x => x.id === item.id)));
    },
  });

  const { mutateAsync } = useMutation(
    ({ id, updatedMembers }: { id: number; updatedMembers: number[] }) =>
      editGroupMembers(id, updatedMembers)
  );

  const { mutateAsync: importMembersMutation } = useMutation(
    ({ id, file }: { file: File; id: number }) => importMembers(id, file),
    {
      onSuccess: (data: TenantEntities['viewers']) => {
        setSelectedMembers(prev => [
          ...prev,
          ...data.filter(item => !prev.some(x => x.email === item.email)),
        ]);
        showToast('success', t('access.import_dialog', { count: data.length }));
      },
      onError: () => showToast('error', t('access.import_error')),
    }
  );

  useEffect(() => {
    if (groupId) {
      void refetch();
    }
  }, [groupId, refetch]);

  const handleAddMember = async (item: TenantEntities['viewers'][number]) => {
    setSelectedMembers(prev => [...prev, item]);
    setMemberEntities(prev => prev.filter(x => x.id !== item.id));
    await mutateAsync({ id: groupId, updatedMembers: [...selectedMembers, item].map(x => x.id) });
  };

  const handleRemoveMember = async (item: TenantEntities['viewers'][number]) => {
    setSelectedMembers(prev => prev.filter(x => x.id !== item.id));
    setMemberEntities(prev => [...prev, item]);
    await mutateAsync({
      id: groupId,
      updatedMembers: selectedMembers.filter(x => x.id !== item.id).map(x => x.id),
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      showToast('success', 'Importing viewers');
      await importMembersMutation({ id: groupId, file }).finally(() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        setIsOpen(false);
        onClose();
      }}
    >
      <DialogContent
        sx={{
          color: theme.palette.brand.textPrimary,
          padding: '0 2rem',
          overflow: 'visible',
        }}
      >
        <HeaderWrapper>
          <Title>{t('access.members')}</Title>
          <StyledButton
            label="Import viewers"
            variant="primary"
            style={{ fontSize: '1.5rem' }}
            onClick={() => fileInputRef.current?.click()}
          />
          <input
            type="file"
            ref={fileInputRef}
            accept=".xlsx, .xls"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </HeaderWrapper>
        {selectedMembers.map(item => (
          <SelectedListItemWrapper key={item.id}>
            <SelectedListItem>
              {(item?.name || item?.lastname) && (
                <p>
                  {item.name} {item.lastname}
                </p>
              )}
              <p>{item.email}</p>
            </SelectedListItem>
            <TrashBinWrapper type="button" onClick={() => handleRemoveMember(item)}>
              <TrashIcon width={15} height={15} />
            </TrashBinWrapper>
          </SelectedListItemWrapper>
        ))}
        <AddViewerButtonWrapper ref={listRef}>
          <AddViewerButton onClick={() => setIsListOpen(!isListOpen)}>
            <p>{t('access.add_to_group')}</p>
            <ChevronDownIcon width={20} height={20} />
          </AddViewerButton>
          {isListOpen && (
            <ViewerList>
              <ListWrapper>
                {memberEntities.length > 0 ? (
                  memberEntities.map(item => (
                    <ListItem key={item.id} id={`add-member#${item.id}`}>
                      {item?.name || item?.lastname ? `${item.name} ${item.lastname}` : item.email}
                    </ListItem>
                  ))
                ) : (
                  <ListItem>{t('empty_list')}</ListItem>
                )}
              </ListWrapper>
            </ViewerList>
          )}
        </AddViewerButtonWrapper>
      </DialogContent>
    </Dialog>
  );
};

const HeaderWrapper = styled.div`
  display: flex;
  padding-bottom: 1rem;
  justify-content: space-between;
  border-bottom: 2px solid ${({ theme }) => theme.palette.brand.backgroundInputFocus};
  margin-bottom: 2rem;
  font-weight: 600;
  align-items: center;
`;

const Title = styled.h3`
  color: ${({ theme }) => theme.palette.brand.primary};
  font-size: 2rem;
`;

const AddViewerButton = styled(Button)`
  background-color: ${({ theme }) => darken(0.1, theme.palette.brand.backgroundDialog)};
  padding: 0.25rem 1.5rem;
  border-radius: 2rem;
  box-shadow: ${({ theme }) => theme.shadows[0]};
  color: ${({ theme }) => theme.palette.brand.white};
  font-size: 1.5rem;
  font-weight: 600;
  text-transform: capitalize;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.palette.brand.primary};
  position: relative;

  :hover {
    background-color: ${({ theme }) => darken(0.2, theme.palette.brand.backgroundDialog)};
  }
`;

const AddViewerButtonWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 1rem;
`;

const ViewerList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: max-content;
  max-height: 250px;
  position: absolute;
  left: 0;
  top: calc(100% + 10px);
  z-index: 100;
  border-radius: 10px;
  padding: 10px;
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows['19']};
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ListItem = styled(Button)`
  font-size: 1.25rem;
  text-transform: none;
  justify-content: start;
  width: 100%;
  padding: 0.5rem 0.5rem;
  margin: 0.25rem 0;
  color: ${({ theme }) => theme.palette.brand.primary};

  :hover {
    background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  }
`;

const SelectedListItem = styled.div`
  font-size: 1.25rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding: 0.5rem 0.5rem;
  margin: 0.25rem 0;
  width: 100%;
  color: ${({ theme }) => theme.palette.brand.primary};
`;

const TrashBinWrapper = styled.button`
  color: ${({ theme }) => theme.palette.brand.primary};
  padding: 0.5rem;
  width: max-content;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 300ms ease-in-out;
  border-radius: 5px;

  :hover {
    background-color: ${({ theme }) => theme.palette.brand.backgroundDialog};
  }
`;

const SelectedListItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export default GroupMembersDialog;
